import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@isophi/core-legacy';
import { ToastService } from '@isophi/core-ui';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs/operators';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { UserService } from '../../../../core/services/user.service';
import { getGroupsFromUIRole, USER_ROLE_OPTIONS, UserRoleOption } from '../../../user/constants/teacher-roles.constants';
import { UserToAdd } from '../../models/user-to-add.model';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddUserComponent {
  protected disabledButton = false;

  protected formError: string | null = null;

  protected firstNameError: string | null = null;

  protected lastNameError: string | null = null;

  protected emailError: string | null = null;

  protected passwordError: string | null = null;

  protected passwordConfirmError: string | null = null;

  protected setGroupsError: string | null = null;

  protected userToAdd: UserToAdd = new UserToAdd();

  protected roleOptions: UserRoleOption[] = USER_ROLE_OPTIONS;

  protected isDirector = false;

  constructor(
    public links: RouterLinkFactory,
    private spinner: NgxSpinnerService,
    private toastr: ToastService,
    private cdRef: ChangeDetectorRef,
    private helperService: HelperService,
    private translocoService: TranslocoService,
    private userService: UserService,
    private router: Router,
    private authService: AuthService
  ) {
    this.helperService.setTitle(null, this.translocoService.translate('user/add'));
    this.userToAdd.generateRandomPassword();

    const user = this.authService.loggedUser;
    this.isDirector = user?.teacher?.isDirector() || false;

    // Redirect if not director (add-user should only be accessible to directors)
    if (!this.isDirector) {
      this.router.navigate(this.links.userList());
      return;
    }

    // Initialize groups for the default role (Teacher)
    this.userToAdd.setGroups = getGroupsFromUIRole(this.userToAdd.selectedRole);
  }

  /**
   * Handle role selection change
   */
  protected onRoleChange(): void {
    this.userToAdd.setGroups = getGroupsFromUIRole(this.userToAdd.selectedRole);
  }

  /**
   * Function to process add user form, when submitted.
   */
  onSubmit(): void {
    this.startProcessingForm();

    // Ensure setGroups is updated based on selected role
    this.userToAdd.setGroups = getGroupsFromUIRole(this.userToAdd.selectedRole);

    this.userService
      .addUser(this.userToAdd)
      .pipe(finalize(() => this.finishProcessingForm()))
      .subscribe({
        next: (response) => {
          this.userToAdd.clear();
          if (response?.userAlreadyExisted) {
            this.toastr.info(this.translocoService.translate('user/userAddAlreadyExistedInfoToast'));
          } else {
            this.toastr.success(this.translocoService.translate('user/userAddSuccessToast'));
          }
          this.router.navigate(this.links.userList());
        },
        error: (error) => {
          const errorData = error.error;

          if (errorData.errors === undefined || error.status === 403) {
            this.formError = this.translocoService.translate('user/userAddErrorToast');
            this.toastr.error(this.formError);
            return;
          }

          for (const errorItem of errorData.errors) {
            if (errorItem.field === 'firstName') {
              this.firstNameError = errorItem.message;
            }
            if (errorItem.field === 'lastName') {
              this.lastNameError = errorItem.message;
            }
            if (errorItem.field === 'email') {
              this.emailError = errorItem.message;
            }
            if (errorItem.field === 'password') {
              this.passwordError = errorItem.message;
            }
            if (errorItem.field === 'passwordConfirm') {
              this.passwordConfirmError = errorItem.message;
            }
            if (errorItem.field === 'setGroups') {
              this.setGroupsError = errorItem.message;
            }
          }
          this.cdRef.detectChanges();
        },
      });
  }

  /**
   * Start processing form.
   */
  protected startProcessingForm(): void {
    this.disabledButton = true;
    this.clearFormErrors();
    this.spinner.show();
  }

  /**
   * Finish processing form.
   */
  protected finishProcessingForm(): void {
    this.spinner.hide();
    this.disabledButton = false;
  }

  /**
   * Clear all errors in form.
   */
  protected clearFormErrors(): void {
    this.formError = null;
    this.firstNameError = null;
    this.lastNameError = null;
    this.emailError = null;
    this.passwordError = null;
    this.passwordConfirmError = null;
    this.setGroupsError = null;
  }
}
